import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Camera, ChevronLeft, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

const LS_DEVICE_ID = "cam.device.id";
const LS_DEVICE_TOKEN = "cam.device.token";
const LS_DEVICE_NAME = "cam.device.name";

function clearLocalEnrollment() {
  try {
    localStorage.removeItem(LS_DEVICE_ID);
    localStorage.removeItem(LS_DEVICE_TOKEN);
    localStorage.removeItem(LS_DEVICE_NAME);
  } catch {
    // Local storage may be unavailable; nothing else is required here.
  }
}

export default function SelectPage() {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, device: null, configured: false, unknown: false });

  const load = useCallback(async () => {
    let deviceId = null;
    let token = null;
    try {
      deviceId = localStorage.getItem(LS_DEVICE_ID);
      token = localStorage.getItem(LS_DEVICE_TOKEN);
    } catch {
      // Treat unavailable local storage as an unenrolled browser.
    }

    if (!deviceId || !token) {
      setState({ loading: false, device: null, configured: false, unknown: false });
      return;
    }

    setState((current) => ({ ...current, loading: true }));
    try {
      const response = await api.get(`/devices/${deviceId}`);
      setState({ loading: false, device: response.data, configured: true, unknown: false });
    } catch (error) {
      if (error?.response?.status === 404) {
        clearLocalEnrollment();
        setState({ loading: false, device: null, configured: false, unknown: true });
        return;
      }
      setState({ loading: false, device: null, configured: true, unknown: false });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reset = () => {
    clearLocalEnrollment();
    setState({ loading: false, device: null, configured: false, unknown: false });
  };

  const approval = state.device?.approval_status;
  const active = approval === "active";
  const pending = approval === "pending";
  const disabled = approval === "disabled";
  const rejected = approval === "rejected";

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white flex items-center justify-center p-4" data-testid="device-camera-page">
      <div className="w-full max-w-xl">
        <Link
          to="/settings"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-5"
          data-testid="device-camera-back"
        >
          <ChevronLeft className="size-4" strokeWidth={1.7} />
          Paramètres
        </Link>

        <section className="rounded-2xl border border-white/[0.08] bg-[#111114] overflow-hidden shadow-2xl shadow-black/20">
          <div className="p-6 sm:p-7 border-b border-white/[0.07]">
            <div className="size-11 rounded-2xl bg-brand/15 text-brand grid place-items-center mb-5">
              <Camera className="size-5" strokeWidth={1.8} />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Utiliser cet appareil comme caméra</h1>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
              Utilisez la caméra de ce téléphone ou de ce PC comme source vidéo dans CAM.
            </p>
          </div>

          <div className="p-6 sm:p-7">
            {state.loading ? (
              <div className="flex items-center gap-3 text-sm text-zinc-400" data-testid="device-camera-loading">
                <Loader2 className="size-4 animate-spin" strokeWidth={1.7} />
                Vérification de cet appareil…
              </div>
            ) : !state.configured ? (
              <>
                {state.unknown && (
                  <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 text-xs text-amber-200">
                    Un ancien enrôlement n’existait plus sur le serveur et a été nettoyé automatiquement.
                  </div>
                )}
                <div className="text-sm font-medium text-zinc-200">Cet appareil n’est pas encore configuré.</div>
                <p className="text-sm text-zinc-500 mt-1.5">La configuration demandera l’accès à sa caméra et un nom.</p>
                <button
                  type="button"
                  onClick={() => nav("/enroll")}
                  className="mt-5 h-11 w-full rounded-xl bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover transition-colors"
                  data-testid="device-camera-configure"
                >
                  Configurer cet appareil
                </button>
              </>
            ) : active ? (
              <>
                <StateLine tone="online" label="Prêt à diffuser" />
                <div className="mt-3 text-sm text-zinc-400">
                  {state.device?.name || "Cet appareil"} est autorisé à envoyer son flux vidéo vers CAM.
                </div>
                <button
                  type="button"
                  onClick={() => nav("/agent")}
                  className="mt-5 h-11 w-full rounded-xl bg-brand px-4 text-sm font-medium text-white hover:bg-brand-hover transition-colors"
                  data-testid="device-camera-start"
                >
                  Démarrer la caméra
                </button>
              </>
            ) : pending ? (
              <>
                <StateLine tone="pending" label="En attente d’autorisation" />
                <p className="mt-3 text-sm text-zinc-400">
                  {state.device?.name || "Cet appareil"} apparaîtra comme caméra dès que sa demande sera approuvée.
                </p>
                <button
                  type="button"
                  onClick={load}
                  className="mt-5 h-11 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-white/[0.09] text-sm font-medium text-zinc-200 hover:bg-white/[0.05] transition-colors"
                  data-testid="device-camera-refresh"
                >
                  <RefreshCw className="size-4" strokeWidth={1.7} />
                  Vérifier à nouveau
                </button>
              </>
            ) : disabled || rejected ? (
              <>
                <StateLine tone="offline" label={disabled ? "Appareil désactivé" : "Demande refusée"} />
                <p className="mt-3 text-sm text-zinc-400">
                  Cet appareil ne peut pas diffuser actuellement. Vous pouvez supprimer sa configuration locale pour recommencer.
                </p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 h-11 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-red-500/25 text-sm font-medium text-red-300 hover:bg-red-500/[0.08] transition-colors"
                  data-testid="device-camera-reset"
                >
                  <Trash2 className="size-4" strokeWidth={1.7} />
                  Réinitialiser cet appareil
                </button>
              </>
            ) : (
              <>
                <StateLine tone="offline" label="État indisponible" />
                <button
                  type="button"
                  onClick={load}
                  className="mt-5 h-11 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-white/[0.09] text-sm font-medium text-zinc-200 hover:bg-white/[0.05] transition-colors"
                >
                  <RefreshCw className="size-4" strokeWidth={1.7} />
                  Réessayer
                </button>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function StateLine({ tone, label }) {
  const dot = tone === "online" ? "bg-emerald-500" : tone === "pending" ? "bg-amber-400" : "bg-red-500";
  const text = tone === "online" ? "text-emerald-300" : tone === "pending" ? "text-amber-200" : "text-red-300";
  return (
    <div className={`flex items-center gap-2 text-sm font-medium ${text}`}>
      <span className={`size-2 rounded-full ${dot}`} />
      {label}
    </div>
  );
}
